import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import ConfirmPopup, { type ConfirmPopupDetails } from "@/UI/popups/ConfirmPopup";
import VerifyOtpPopup, { type VerifyOtpPopupDetails } from "@/UI/popups/VerifyOtpPopup";
import { MenuItem, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useRef, useState } from "react";
import PasswordInput from "../../inputs/PasswordInput";
import "./styles.css";

// ── MUI Shared Styles ─────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    flex: 1,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "14px",
        color: "var(--color-gray-03)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
            color: "var(--color-orange)",
        },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "14px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "8px",
        height: "50px",
        "& fieldset": {
            borderColor: "var(--color-orange-30)",
            borderWidth: "1px",
        },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": {
            borderColor: "var(--color-orange)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px !important",
        paddingRight: dir === "rtl" ? "14px !important" : "32px !important",
        paddingLeft: dir === "rtl" ? "32px !important" : "14px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-icon": {
        right: dir === "rtl" ? "unset" : 7,
        left: dir === "rtl" ? 7 : "unset",
    },
});

const datePickerSx = {
    flex: 1,
    "& .MuiPickersInputBase-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "14px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "8px",
        height: "50px",
        flexDirection: "row-reverse",
        "& fieldset": {
            borderColor: "var(--color-orange-30)",
            borderWidth: "1px",
        },
        "&:hover fieldset": {
            borderColor: "var(--color-orange)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
        "& .MuiPickersSectionList-root": {
            justifyContent: "flex-end",
        },
        "& .MuiInputAdornment-root": {
            marginLeft: 0,
        },
    },
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface ClientData {
    _id: string;
    name: string;
    phone: string;
    email?: string;
    picture?: string;
    birthDate?: string;
    gender?: "male" | "female";
}

interface FormState {
    name: string;
    email: string;
    birthDate: string;
    gender: "male" | "female" | "";
    pictureFile: File | null;
    picturePreview: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function SettingsScreen() {
    const { lang, setLang } = useLangStore();
    const { addComp, removeComp } = usePopupStore();
    const t = translations[lang ?? "ar"];
    const fileInputRef = useRef<HTMLInputElement>(null);

    const textFieldSx = getTextFieldSx(lang === "ar" ? "rtl" : "ltr");

    // ── Fetch client data ────────────────────────────────────────────────────────

    const { data, loading } = useFetch("clients/me", { method: "GET" });
    const client: ClientData | null = data?.data?.client ?? null;

    // ── Account form state ─────────────────────────────────────────────────────

    const [form, setForm] = useState<FormState>({
        name: "",
        email: "",
        birthDate: "",
        gender: "",
        pictureFile: null,
        picturePreview: "",
    });
    const [hasChanges, setHasChanges] = useState(false);

    // ── Language state ─────────────────────────────────────────────────────────

    const [selectedLang, setSelectedLang] = useState<"ar" | "en">(lang ?? "ar");
    const hasLangChanges = selectedLang !== lang;

    // ── Password state ─────────────────────────────────────────────────────────

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const hasPasswordChanges = password.trim().length > 0 || confirmPassword.trim().length > 0;

    // ── Helpers ─────────────────────────────────────────────────────────────────

    const showMessage = (message: string) => {
        const name = "settings-message";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message,
                confirmLabel: t.tierForm?.ok ?? "OK",
                hideCancel: true,
                onConfirm: () => removeComp(name),
            } as ConfirmPopupDetails,
        });
    };

    const showError = (message: string) => showMessage(message);
    const showSuccess = (message: string) => showMessage(message);

    // ── Populate form on data load ─────────────────────────────────────────────

    useEffect(() => {
        if (client) {
            setForm({
                name: client.name ?? "",
                email: client.email ?? "",
                birthDate: client.birthDate ? client.birthDate.split("T")[0] : "",
                gender: client.gender ?? "",
                pictureFile: null,
                picturePreview: client.picture ?? "",
            });
        }
    }, [client]);

    // ── Track changes ──────────────────────────────────────────────────────────

    useEffect(() => {
        if (!client) return;
        const changed =
            form.name !== (client.name ?? "") ||
            form.email !== (client.email ?? "") ||
            form.birthDate !== (client.birthDate ? client.birthDate.split("T")[0] : "") ||
            form.gender !== (client.gender ?? "") ||
            form.pictureFile !== null;
        setHasChanges(changed);
    }, [form, client]);

    // ── Update API ─────────────────────────────────────────────────────────────

    const { invoke: submitUpdate, loading: submitting, status: submitStatus, error: submitError } = useFetch("clients/me", { method: "PUT" }, false);

    const wasSubmitting = useRef(false);

    useEffect(() => {
        if (wasSubmitting.current && !submitting) {
            wasSubmitting.current = false;
            if (submitStatus === 200) {
                showSuccess(t.settings.updateSuccess);
                setHasChanges(false);
            } else if (submitError) {
                showError(submitError);
            }
        }
    }, [submitting]);

    // ── Handlers ───────────────────────────────────────────────────────────────

    const handlePictureClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setForm((prev) => ({
                ...prev,
                pictureFile: file,
                picturePreview: URL.createObjectURL(file),
            }));
        }
    };

    const handleAccountSave = () => {
        if (submitting) return;

        const formData = new FormData();
        formData.append("name", form.name);
        formData.append("email", form.email);
        formData.append("birthDate", form.birthDate);
        if (form.gender) formData.append("gender", form.gender);

        if (form.pictureFile) {
            formData.append("newPicture", form.pictureFile);
        } else if (form.picturePreview) {
            formData.append("picture", form.picturePreview);
        }

        wasSubmitting.current = true;
        submitUpdate({ formData });
    };

    const handleAccountCancel = () => {
        if (!client) return;
        setForm({
            name: client.name ?? "",
            email: client.email ?? "",
            birthDate: client.birthDate ? client.birthDate.split("T")[0] : "",
            gender: client.gender ?? "",
            pictureFile: null,
            picturePreview: client.picture ?? "",
        });
    };

    const handleLanguageSave = () => {
        setLang(selectedLang);
    };

    const handleLanguageCancel = () => {
        setSelectedLang(lang ?? "ar");
    };

    // ── Password Reset ─────────────────────────────────────────────────────────

    const {
        loading: resetting,
        status: resetStatus,
        error: resetError,
        data: resetData,
        invoke: resetPassword,
    } = useFetch("auth/reset-pass", { method: "PUT" }, false);

    const openVerifyOtpPopup = (operationId: string) => {
        const name = "verify-otp";
        removeComp(name);
        addComp({
            name,
            Component: VerifyOtpPopup,
            details: {
                operationId,
                onSuccess: () => {
                    setPassword("");
                    setConfirmPassword("");
                    removeComp(name);
                },
            } as VerifyOtpPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: false,
        });
    };

    const wasResetting = useRef(false);
    useEffect(() => {
        if (resetting) {
            wasResetting.current = true;
        } else if (wasResetting.current) {
            wasResetting.current = false;
            if (resetStatus === 200) {
                const operationId = resetData?.data?.operationId;
                if (operationId) {
                    openVerifyOtpPopup(operationId);
                }
            } else if (resetError) {
                showError(resetError);
            }
        }
    }, [resetting, resetStatus]);

    const handlePasswordSave = () => {
        if (resetting) return;

        // Validation
        if (password.trim().length < 6) {
            showError(t.settings.passwordTooShort);
            return;
        }

        if (password !== confirmPassword) {
            showError(t.settings.passwordMismatch);
            return;
        }

        wasResetting.current = true;
        resetPassword({ data: { newPassword: password } });
    };

    const handlePasswordCancel = () => {
        setPassword("");
        setConfirmPassword("");
    };

    // ── Loading state ──────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="settings-screen settings-screen--loading">
                <div className="settings-spinner" />
            </div>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className="settings-screen">
                {/* ══════════════════════════════════════════════════════════════════════
                Account & Security Section
            ══════════════════════════════════════════════════════════════════════ */}
                <div className="settings-section">
                    <div className="settings-section-header">
                        <div className="settings-section-icon-wrap">
                            <img src="/icons/client-icon.svg" alt="" className="settings-section-icon-inner" />
                        </div>
                        <span className="settings-section-title">{t.settings.accountSection}</span>
                    </div>

                    <div className="settings-section-content">
                        {/* Picture */}
                        <div className="settings-picture-box">
                            <div className="settings-avatar-wrap">
                                {form.picturePreview ? (
                                    <img src={form.picturePreview} alt={form.name} className="settings-avatar-img" />
                                ) : (
                                    <span className="settings-avatar-letter">{form.name?.charAt(0) ?? "?"}</span>
                                )}
                            </div>
                            <button type="button" className="settings-change-picture-btn" onClick={handlePictureClick}>
                                {t.settings.changePicture}
                            </button>
                            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: "none" }} />
                        </div>

                        {/* Name & Email Row */}
                        <div className="settings-row">
                            <div className="settings-field">
                                <label className="settings-label">{t.settings.changeName}</label>
                                <div className="settings-input-wrap">
                                    <input
                                        type="text"
                                        className="settings-input"
                                        value={form.name}
                                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                        dir="auto"
                                    />
                                </div>
                            </div>
                            <div className="settings-field">
                                <label className="settings-label">{t.settings.changeEmail}</label>
                                <div className="settings-input-wrap">
                                    <input
                                        type="email"
                                        className="settings-input"
                                        value={form.email}
                                        onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                                        dir="ltr"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Gender & BirthDate Row */}
                        <div className="settings-row">
                            <div className="settings-field">
                                <label className="settings-label">{t.settings.selectGender}</label>
                                <TextField
                                    select
                                    value={form.gender}
                                    onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value as "male" | "female" | "" }))}
                                    size="small"
                                    sx={textFieldSx}
                                >
                                    <MenuItem value="">&nbsp;</MenuItem>
                                    <MenuItem value="male">{t.settings.male}</MenuItem>
                                    <MenuItem value="female">{t.settings.female}</MenuItem>
                                </TextField>
                            </div>
                            <div className="settings-field">
                                <label className="settings-label">{t.settings.birthDate}</label>
                                <DatePicker
                                    value={form.birthDate ? new Date(form.birthDate) : null}
                                    onChange={(date) => {
                                        if (date) {
                                            const year = date.getFullYear();
                                            const month = String(date.getMonth() + 1).padStart(2, "0");
                                            const day = String(date.getDate()).padStart(2, "0");
                                            setForm((p) => ({ ...p, birthDate: `${year}-${month}-${day}` }));
                                        } else {
                                            setForm((p) => ({ ...p, birthDate: "" }));
                                        }
                                    }}
                                    sx={datePickerSx}
                                    slotProps={{
                                        textField: { size: "small" },
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Account Actions */}
                    <div className="settings-actions">
                        <button type="button" className="settings-cancel-btn" onClick={handleAccountCancel} disabled={!hasChanges}>
                            {t.settings.cancel}
                        </button>
                        <button type="button" className="settings-save-btn" onClick={handleAccountSave} disabled={!hasChanges || submitting}>
                            {t.settings.saveChanges}
                        </button>
                    </div>
                </div>

                {/* ══════════════════════════════════════════════════════════════════════
                Language & Format Section
            ══════════════════════════════════════════════════════════════════════ */}
                <div className="settings-section">
                    <div className="settings-section-header">
                        <div className="settings-section-icon-wrap">
                            <img src="/icons/settings-icon.svg" alt="" className="settings-section-icon-inner" />
                        </div>
                        <span className="settings-section-title">{t.settings.languageSection}</span>
                    </div>

                    <div className="settings-section-content">
                        <div className="settings-row settings-row--single">
                            <div className="settings-field settings-field--language">
                                <label className="settings-label">{t.settings.selectLanguage}</label>
                                <TextField
                                    select
                                    value={selectedLang}
                                    onChange={(e) => setSelectedLang(e.target.value as "ar" | "en")}
                                    size="small"
                                    sx={textFieldSx}
                                >
                                    <MenuItem value="ar">{t.settings.arabic}</MenuItem>
                                    <MenuItem value="en">{t.settings.english}</MenuItem>
                                </TextField>
                            </div>
                        </div>
                    </div>

                    {/* Language Actions */}
                    <div className="settings-actions">
                        <button type="button" className="settings-cancel-btn" onClick={handleLanguageCancel} disabled={!hasLangChanges}>
                            {t.settings.cancel}
                        </button>
                        <button type="button" className="settings-save-btn" onClick={handleLanguageSave} disabled={!hasLangChanges}>
                            {t.settings.saveChanges}
                        </button>
                    </div>
                </div>

                {/* ══════════════════════════════════════════════════════════════════════
                Change Password Section (UI Only)
            ══════════════════════════════════════════════════════════════════════ */}
                <div className="settings-section">
                    <div className="settings-section-header">
                        <div className="settings-section-icon-wrap">
                            <img src="/icons/lock-icon.svg" alt="" className="settings-section-icon-inner" />
                        </div>
                        <span className="settings-section-title">{t.settings.passwordSection}</span>
                    </div>

                    <div className="settings-section-content">
                        <div className="settings-row">
                            <div className="settings-field">
                                <label className="settings-label">{t.settings.password}</label>
                                <PasswordInput value={password} onChange={setPassword} placeholder={"**************"} disabled={loading} />
                            </div>
                            <div className="settings-field">
                                <label className="settings-label">{t.settings.confirmPassword}</label>
                                <PasswordInput
                                    value={confirmPassword}
                                    onChange={setConfirmPassword}
                                    placeholder={"**************"}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Password Actions */}
                    <div className="settings-actions">
                        <button
                            type="button"
                            className="settings-cancel-btn"
                            onClick={handlePasswordCancel}
                            disabled={!hasPasswordChanges || resetting}
                        >
                            {t.settings.cancel}
                        </button>
                        <button type="button" className="settings-save-btn" onClick={handlePasswordSave} disabled={!hasPasswordChanges || resetting}>
                            {t.settings.saveChanges}
                        </button>
                    </div>
                </div>
            </div>
        </LocalizationProvider>
    );
}
